import { i18n } from "@lingui/core";
import { WIcon } from "@warp-ds/components/react/icon";
import { Button } from "@warp-ds/react";
import { useState } from "react";
import { trackSemanticSearchFeedbackClick } from "../../tracking/pulse";
import { postSemanticFeedback } from "../search/semanticClient";

type FeedbackPromptProps = {
	query: string;
	onFeedbackGiven?: () => void;
	locale: string;
};

const CONTAINER_CLASS =
	"flex justify-center items-center content-center gap-24 self-stretch rounded-8 px-32 py-16 s-bg-info-subtle";

function getFollowUpMessage(feedbackType: "positive" | "negative"): string {
	if (feedbackType === "positive") {
		return i18n.t({
			id: "explore.results.feedback.followup.positive",
			message: "Thanks! Do you want to tell us more about what you liked?",
		});
	}
	return i18n.t({
		id: "explore.results.feedback.followup.negative",
		message: "Thanks! Do you want to tell us more about what you didn't like?",
	});
}

export function FeedbackPrompt(props: FeedbackPromptProps) {
	const { query, onFeedbackGiven } = props;
	const [feedbackType, setFeedbackType] = useState<"positive" | "negative" | null>(null);
	const [feedbackUrl, setFeedbackUrl] = useState<string | null>(null);

	const handleInitialFeedback = async (variant: "positive" | "negative") => {
		trackSemanticSearchFeedbackClick(query, variant);
		const feedbackValue = variant === "positive" ? "good" : "bad";
		const url = await postSemanticFeedback(query, feedbackValue);

		if (url) {
			setFeedbackUrl(url);
		}
		setFeedbackType(variant);
	};

	const handleFollowUpClick = () => {
		if (feedbackType) {
			// TODO Add follow up click tracking
			//trackSemanticSearchFeedbackFollowUp(query, feedbackType, "submit");
		}
		onFeedbackGiven?.();
	};

	if (feedbackType) {
		const followUpMessage = getFollowUpMessage(feedbackType);

		return (
			<div className={`${CONTAINER_CLASS} flex-col md:flex-row flex-wrap`}>
				<span className="t4 font-medium">{followUpMessage}</span>
				<Button
					onClick={handleFollowUpClick}
					className="flex flex-row items-center rounded-8 border-none"
					href={feedbackUrl ?? ""}
					target="_blank"
				>
					<WIcon name="Messages" size="small" locale={i18n.locale} className="flex mr-8" />
					{i18n.t({
						id: "explore.results.feedback.followup.button",
						message: "Give feedback",
					})}
				</Button>
			</div>
		);
	}

	return (
		<div className={`${CONTAINER_CLASS} flex-row md:flex-wrap`}>
			<span className="t4 font-medium">
				{i18n.t({
					id: "explore.results.feedback.prompt",
					message: "What do you think about Explore?",
				})}
			</span>
			<div className="flex gap-8">
				<Button
					onClick={() => handleInitialFeedback("positive")}
					className="rounded-full border-none"
					aria-label={i18n.t({
						id: "explore.results.feedback.positive.label",
						message: "Positive feedback",
					})}
				>
					<WIcon name="Like" size="small" locale={i18n.locale} />
				</Button>
				<Button
					onClick={() => handleInitialFeedback("negative")}
					className="p-14 rounded-full border-none"
					aria-label={i18n.t({
						id: "explore.results.feedback.negative.label",
						message: "Negative feedback",
					})}
				>
					<WIcon name="Dislike" size="small" locale={i18n.locale} />
				</Button>
			</div>
		</div>
	);
}
